import { Fragment } from 'react';

import {
  CampaignDayDelayPure,
  CampaignWaitForConnectPure,
  CampaignWaitForConnectYesBanner,
} from '@poseidon/components/CampaignDayCard';

import type { SequenceTimelineBlock } from '~/components/Sequence/sequenceTimeline';
import { SEQUENCE_SWITCH_ATTRIBUTES } from '~/components/Sequence/sequenceSteps';
import { SequenceTimelineCard } from '~/components/Sequence/SequenceTimelineCard';
import { SequenceTimelineDay } from '~/components/Sequence/SequenceTimelineDay';
import { SequenceTimelineRoute } from '~/components/Sequence/SequenceTimelineRoute';

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * A line of a contact's sequence timeline, top to bottom, recursing into the lanes nested in its cards. The
 * campaign's own wait pill carries its connector; every other gap gets the campaign's short centred spine.
 */
export function SequenceTimelineBlocks({ blocks }: { blocks: SequenceTimelineBlock[] }) {
  return (
    <div className="flex flex-col">
      {blocks.map((block, k) => (
        <Fragment key={block.key}>
          {k > 0 && block.kind !== 'wait' && blocks[k - 1]?.kind !== 'wait' && (
            <span aria-hidden className="mx-auto block h-3 w-px bg-lynch-200 dark:bg-slate-700" />
          )}
          {block.kind === 'day' && <SequenceTimelineDay date={block.date} steps={block.steps} />}
          {block.kind === 'wait' && <CampaignDayDelayPure days={block.days} />}
          {block.kind === 'await' && (
            <>
              <CampaignWaitForConnectPure
                delay={block.deadlineDays * DAY_MS}
                state={block.state}
                autoWithdraw={block.autoWithdraw}
                noBranchChildren={block.no.length > 0 ? <SequenceTimelineBlocks blocks={block.no} /> : undefined}
              />
              {/* Labels what follows as the accepted path — only when something does follow. */}
              {k < blocks.length - 1 && <CampaignWaitForConnectYesBanner />}
            </>
          )}
          {block.kind === 'spawn' && (
            <SequenceTimelineCard title="Runs alongside">
              <div className="px-3 py-2.5">
                <SequenceTimelineBlocks blocks={block.blocks} />
              </div>
            </SequenceTimelineCard>
          )}
          {block.kind === 'switch' && (
            <SequenceTimelineCard title={`Which route? · ${SEQUENCE_SWITCH_ATTRIBUTES[block.attribute].label}`}>
              {block.routes.map((route, r) => (
                <SequenceTimelineRoute
                  key={r}
                  label={route.label}
                  notTaken={route.notTaken}
                  endsHere={route.empty && !route.notTaken}>
                  {route.blocks.length > 0 && <SequenceTimelineBlocks blocks={route.blocks} />}
                </SequenceTimelineRoute>
              ))}
            </SequenceTimelineCard>
          )}
        </Fragment>
      ))}
    </div>
  );
}
